/**
 * Hero Slider — v4 流光接力（Color-Flow Crossfade，2026-06-01）
 *
 * JS 比重瘦身至 ~15%：色彩流動與 FX crossfade 全交 CSS（@property 原生補間 + class 驅動的
 * keyframe），JS 不再做任何 rAF 逐幀補間、不寫任何 inline style。本檔只負責：
 *   1. data-current-slide 鏡像 .hero + .hero-immersion（CSS 屬性選擇器接此切色 / 切 FX）
 *   2. FX 三層 .is-fx-active 切換（+ 換場瞬間 .is-fx-leaving / .is-fx-entering 跑 crossfade）
 *   3. 舊文字塊 .is-hero-leaving 淡出 → 淡完設 hidden；新塊 is-hero-revealed stagger 接力
 *      （--hero-reveal-offset:200ms 讓 stagger 起點接舊塊淡出尾）
 *   4. indicators / autoplay / swipe / keyboard 全保留 + goTo in-progress 鎖（防快速連點錯亂）
 *   5. 切 slide 發 hero:slidechange 事件 → hero-immersion.js 對新 slide icon 重量 FX 發散中心
 *
 * 底色：.hero 永遠透明，底色由 .hero-immersion 的 @property 補間提供（見 hero-immersion.css）。
 * prefers-reduced-motion：關閉 autoplay；視覺退化純由 CSS media query 處理（本檔不分支）。
 */

// 共用 util；別名避免與本檔讀一次的布林區域變數 prefersReducedMotion 撞名（語義仍是讀一次）
import { prefersReducedMotion as prefersReducedMotionMQ } from './utils/dom.js';

const hero = document.querySelector('.hero');
if (hero) {
  // 沉浸底色層：data-current-slide 鏡像至此（per-slide 底色 / 環色 / FX 選擇器都掛這層）。
  const immersion = document.querySelector('.hero-immersion');
  const fxLayers = immersion
    ? Array.from(immersion.querySelectorAll('.hero-immersion__fx-layer'))
    : [];

  const contents = Array.from(hero.querySelectorAll('.hero__content[data-slide-content]'));
  const indicators = Array.from(hero.querySelectorAll('.hero__indicator'));
  const prevBtn = hero.querySelector('.hero__chevron--prev');
  const nextBtn = hero.querySelector('.hero__chevron--next');

  if (contents.length > 1) {
    const total = contents.length;
    let currentIdx = parseInt(hero.dataset.currentSlide ?? '0', 10);
    if (Number.isNaN(currentIdx) || currentIdx < 0 || currentIdx >= total) {
      currentIdx = 0;
    }

    const prefersReducedMotion = prefersReducedMotionMQ();

    // 換場節拍（對齊 CSS）：舊文字塊淡出 350ms / FX collapse 550ms。
    // 鎖到較長的 550ms：防快速連點時 class 交疊錯亂（emerge 1100ms 仍在跑也無妨，
    // is-fx-active 已 hold 終態，下次切換重跑 crossfade 視覺仍順）。
    const TEXT_LEAVE_MS = 350;
    const FX_CROSSFADE_MS = 550;
    const REVEAL_OFFSET = '200ms';   // 新塊 stagger 起點偏移（接舊塊淡出尾）

    // 切片設 FX active：data-fx="idx" 的層加 is-fx-active，其餘移除
    const setFxActive = (idx) => {
      fxLayers.forEach((layer) => {
        layer.classList.toggle('is-fx-active', layer.dataset.fx === String(idx));
      });
    };

    // 初始鏡像 data-current-slide + FX active（FX per-slide 選擇器掛在 immersion）
    if (immersion) immersion.dataset.currentSlide = String(currentIdx);
    setFxActive(currentIdx);

    // 初始 reveal：當前 slide 套 is-hero-revealed（page load 進場一次性，無 offset）
    contents.forEach((el, i) => {
      el.hidden = i !== currentIdx;
    });
    // rAF 確保 hidden → visible 切換後 reveal class 觸發 transition
    requestAnimationFrame(() => {
      contents[currentIdx]?.classList.add('is-hero-revealed');
      // LCP 修復（2026-06-26）：首屏進場已由 CSS 於載入即自播（hero.css 的 hero-rv-* /
      // @media no-preference），不再等本模組（deferred main.js 鏈）才顯影。JS 接手後加
      // is-hero-ready 關掉自播，避免後續 slide 切換與自播雙重進場；與上行同一 tick 加，
      // 瀏覽器單次 style recalc 內 is-hero-revealed(opacity:1) 接住自播終態 → 無閃爍。
      hero.classList.add('is-hero-ready');
    });

    // goTo in-progress 鎖 + 待清理的 transient class timer（每次切換先清舊 timer 防 stale 清除）
    let isTransitioning = false;
    let cleanupTimer = null;

    const goTo = (idx) => {
      const next = ((idx % total) + total) % total;
      if (next === currentIdx) return;
      if (isTransitioning) return;   // in-progress 鎖：換場中忽略新請求
      isTransitioning = true;
      window.clearTimeout(cleanupTimer);

      const oldContent = contents[currentIdx];
      const newContent = contents[next];
      // focus 切換：先記舊 content 是否含焦點，切完後若有則 focus 新 content 的可互動元素
      const oldHasFocus = oldContent ? oldContent.contains(document.activeElement) : false;

      // 1. 舊文字塊：淡出（is-hero-leaving 取代直接 hidden）→ 淡完才 hidden
      oldContent?.classList.remove('is-hero-revealed');
      oldContent?.classList.add('is-hero-leaving');

      // 2. 新文字塊：顯示 + 設 reveal offset（stagger 起點接舊塊淡出尾）
      newContent.hidden = false;
      newContent.style.setProperty('--hero-reveal-offset', REVEAL_OFFSET);

      // 3. data-current-slide 鏡像（CSS @property 底色/環色補間 + FX per-slide 接此）
      hero.dataset.currentSlide = String(next);
      if (immersion) immersion.dataset.currentSlide = String(next);

      // 4. FX 三層 crossfade：舊層 collapse / 新層 emerge，並切 is-fx-active 定終態可見
      const oldFx = fxLayers.find((l) => l.dataset.fx === String(currentIdx));
      const newFx = fxLayers.find((l) => l.dataset.fx === String(next));
      oldFx?.classList.remove('is-fx-active');
      oldFx?.classList.add('is-fx-leaving');
      newFx?.classList.add('is-fx-entering', 'is-fx-active');

      // 5. 發 hero:slidechange → hero-immersion.js 對「新 slide 的 icon」重量 FX 發散中心
      hero.dispatchEvent(new CustomEvent('hero:slidechange', {
        bubbles: true,
        detail: { index: next },
      }));

      // 6. 新塊 reveal：雙 rAF 確保 hidden → visible reflow 完成後再加 reveal class
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          newContent.classList.add('is-hero-revealed');
        });
      });

      // 7. indicators 狀態同步
      indicators.forEach((d, i) => {
        const isActive = i === next;
        d.classList.toggle('hero__indicator--active', isActive);
        d.setAttribute('aria-selected', String(isActive));
      });

      // 8. focus 跟著 visible content 走
      if (oldHasFocus) {
        newContent.querySelector('a, button')?.focus();
      }

      currentIdx = next;

      // 9. 換場收尾：舊塊淡完設 hidden、清掉舊塊 leaving + 新塊 entering transient class、解鎖。
      //    --hero-reveal-offset 刻意「不清」：reveal transition 的 delay 在啟動瞬間即已採樣，
      //    此時清除無法回溯改變正在跑的 reveal，反而可能在部分引擎觸發 delay 重算。留著也語意正確
      //    ——只有「初始載入時顯示的那塊」該無 offset（140ms 起），任何經過換場的塊都該帶 +200ms。
      cleanupTimer = window.setTimeout(() => {
        oldContent?.classList.remove('is-hero-leaving');
        if (oldContent) oldContent.hidden = true;
        oldFx?.classList.remove('is-fx-leaving');
        newFx?.classList.remove('is-fx-entering');
        isTransitioning = false;
        // 舊塊已 hidden、新塊回最終位置 → 再發一次讓 immersion 校正發散中心（治 mobile 換場期暫時偏移）
        hero.dispatchEvent(new CustomEvent('hero:slidechange', {
          bubbles: true,
          detail: { index: next, settled: true },
        }));
      }, Math.max(TEXT_LEAVE_MS, FX_CROSSFADE_MS));
    };

    prevBtn?.addEventListener('click', () => goTo(currentIdx - 1));
    nextBtn?.addEventListener('click', () => goTo(currentIdx + 1));

    indicators.forEach((d, i) => {
      d.addEventListener('click', () => goTo(i));
    });

    // 鍵盤左右箭頭（focus 在 hero 內任一互動元件時有效）
    hero.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        goTo(currentIdx - 1);
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        goTo(currentIdx + 1);
      }
    });

    // Autoplay: 8s 自動切換下一張，hover / 頁面隱藏暫停，手動操作後重置 timer。
    // 手機（≤767.98）停用 autoplay：只靠 swipe + 點點手動切換（桌機/平板維持 8s）。
    const AUTOPLAY_INTERVAL = 8000;
    const mobileMQ = window.matchMedia('(max-width: 767.98px)');
    let autoplayTimer = null;

    const startAutoplay = () => {
      if (autoplayTimer) return;
      if (prefersReducedMotion) return;
      if (mobileMQ.matches) return;   // 手機不啟動 autoplay
      autoplayTimer = window.setInterval(() => {
        goTo(currentIdx + 1);
      }, AUTOPLAY_INTERVAL);
    };

    const stopAutoplay = () => {
      if (autoplayTimer) {
        clearInterval(autoplayTimer);
        autoplayTimer = null;
      }
    };

    const resetAutoplay = () => {
      stopAutoplay();
      startAutoplay();
    };

    startAutoplay();

    // 斷點跨越（resize / 平板旋轉）：進手機停 autoplay、回桌機/平板重啟（仍受 PRM / visibility 守門）
    mobileMQ.addEventListener('change', (e) => {
      if (e.matches) stopAutoplay();
      else startAutoplay();
    });

    // hover 暫停
    hero.addEventListener('mouseenter', stopAutoplay);
    hero.addEventListener('mouseleave', startAutoplay);

    // 手動操作重置 timer
    prevBtn?.addEventListener('click', resetAutoplay);
    nextBtn?.addEventListener('click', resetAutoplay);
    indicators.forEach((d) => {
      d.addEventListener('click', resetAutoplay);
    });
    hero.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') resetAutoplay();
    });

    // === Touch swipe support（mobile：取代 chevron 互動） ===
    const SWIPE_THRESHOLD = 50;
    let touchStartX = 0;
    let touchStartY = 0;

    hero.addEventListener('touchstart', (e) => {
      touchStartX = e.changedTouches[0].screenX;
      touchStartY = e.changedTouches[0].screenY;
    }, { passive: true });

    hero.addEventListener('touchend', (e) => {
      const touchEndX = e.changedTouches[0].screenX;
      const touchEndY = e.changedTouches[0].screenY;

      const deltaX = touchEndX - touchStartX;
      const deltaY = touchEndY - touchStartY;

      // 排除垂直滑動（避免與 page scroll 衝突）
      if (Math.abs(deltaY) > Math.abs(deltaX)) return;
      if (Math.abs(deltaX) < SWIPE_THRESHOLD) return;

      if (deltaX > 0) {
        goTo(currentIdx - 1);
      } else {
        goTo(currentIdx + 1);
      }
      resetAutoplay();
    }, { passive: true });

    // 頁面隱藏暫停（節電 + 避免背景跑）
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) stopAutoplay();
      else startAutoplay();
    });
  }
}
